/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Dialog as MuiDialog,
  DialogContent,
  DialogActions,
  IconButton,
  DialogTitle,
  Button,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ColumnFlexBox } from '../FlexBox';

const Dialog = function Dialog({
  id,
  open = false,
  onClose = () => {},
  children,
  title = '',
  titleColor = '',
  styles,
  icon = undefined,
  width = 'md',
  fullScreen = false,
  cancelLabel = 'CANCEL',
  submitLabel = 'CONFIRM',
  actionPosition = 'flex-end',
  onSubmit,
  onCancel,
  form = false,
  disableValidation = false,
  enctype,
  disableSubmit = false,
  disableCancel = false,
  dividers = false,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  submitButtonProps = {},
  hideCloseIcon = false,
  padding = '16px',
  fullWidth,
  Transition,
  hideSubmitButton = false,
  overTitle,
}) {
  const intl = useIntl();
  const methods = useForm();

  useEffect(() => {
    if (!open) {
      const defaultValues = methods.getValues();
      Object.keys(defaultValues)?.forEach((key) => {
        methods.setValue(key, null);
      });
    }
  }, [open]);

  const translate = (txt) => {
    return intl.messages[txt?.toUpperCase()] ?? txt;
  };

  // eslint-disable-next-line no-unused-vars
  const onError = (err, e) => {
    // eslint-disable-next-line no-console
    console.log(err);
  };

  const handleSubmit = (formData) => {
    const parsedData = Object.fromEntries(formData); //converts formData into a Record<string,any>.
    if (onSubmit) onSubmit(parsedData);
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onClose });
    }
    return child;
  });
  const FormContent = (
    <>
      <DialogContent
        dividers={dividers}
        sx={{
          display: 'flex',
          padding,
          ...styles?.content,
        }}
      >
        {childrenWithProps}
      </DialogContent>
      {((onSubmit && !hideSubmitButton) || onCancel) && (
        <DialogActions
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: actionPosition,
            gap: '12px',
            padding: '12px',
          }}
        >
          {onCancel && (
            <Button
              id="dialog-cancel-btn"
              disableElevation
              variant="outlined"
              sx={{ width: '200px' }}
              disabled={disableCancel}
              onClick={onCancel}
            >
              {cancelLabel && translate(cancelLabel)}
            </Button>
          )}
          {onSubmit && !hideSubmitButton && (
            <Button
              id="dialog-submit-btn"
              disableElevation
              type={form ? 'submit' : 'button'}
              sx={{ width: '200px', whiteSpace: 'nowrap' }}
              disabled={disableSubmit}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...submitButtonProps}
              onClick={
                form
                  ? undefined
                  : (e) => {
                      e.preventDefault();
                      if (!form) onSubmit();
                    }
              }
            >
              {submitLabel && translate(submitLabel)}
            </Button>
          )}
        </DialogActions>
      )}
    </>
  );

  return (
    <FormProvider {...methods}>
      <MuiDialog
        id={id}
        open={open}
        fullScreen={fullScreen}
        onClose={
          disableBackdropClick
            ? (_, reason) => {
                if (reason !== 'backdropClick') {
                  onClose();
                }
              }
            : onClose
        }
        maxWidth={width}
        fullWidth={fullWidth}
        sx={{
          ...styles?.root,
          '& .MuiDialog-paper': { borderRadius: fullScreen ? 'unset' : '18px' },
        }}
        TransitionComponent={Transition ?? undefined}
        transitionDuration={500}
        disableEscapeKeyDown={disableEscapeKeyDown}
      >
        {title && (
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              userSelect: 'none',
              /* backgroundColor: 'primary.main',
              color: '#fff', */
              ...styles?.title,
            }}
          >
            {icon}
            <ColumnFlexBox flexGrow={1}>
              <Typography variant="button" color="text.secondary">
                {translate(overTitle)}
              </Typography>
              {typeof title === 'string' ? (
                <Typography
                  variant="h6"
                  fontSize="24px"
                  color={titleColor ?? 'inherit'}
                >
                  {translate(title)}
                </Typography>
              ) : (
                title
              )}
            </ColumnFlexBox>
            {!hideCloseIcon && (
              <IconButton
                onClick={onClose}
                color="inherit"
                sx={{ marginLeft: 'auto' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
        )}

        {form ? (
          <form
            style={{
              display: 'contents', //contents does not creates form element in DOM
            }}
            onSubmit={
              disableValidation
                ? (e) => {
                    e.preventDefault();
                    onSubmit(methods.getValues());
                  }
                : methods?.handleSubmit(onSubmit, onError)
            }
            action={handleSubmit}
            encType={enctype}
            noValidate
          >
            {FormContent}
          </form>
        ) : (
          FormContent
        )}
      </MuiDialog>
    </FormProvider>
  );
};

Dialog.propTypes = {
  id: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  styles: PropTypes.object,
  icon: PropTypes.element,
  width: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullScreen: PropTypes.bool,
  cancelLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  aspectRatio: PropTypes.string,
  actionPosition: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  form: PropTypes.bool,
  disableValidation: PropTypes.bool,
  enctype: PropTypes.string,
  disableSubmit: PropTypes.bool,
  disableCancel: PropTypes.bool,
  dividers: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  submitButtonProps: PropTypes.object,
  hideCloseIcon: PropTypes.bool,
  scrollbarGutter: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  backgroundColor: PropTypes.string,
  Transition: PropTypes.elementType,
  fullWidth: PropTypes.bool,
};

export default Dialog;
